import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Loader2, Search, X, RotateCcw, RefreshCw, Upload, Download, 
  FileText, Image, File, Eye, Trash2, Edit, FolderOpen, Tag, 
  Building2, Users, Filter, MoreVertical, Replace
} from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const TEAM_TAGS = [
  { value: 'all', label: 'All Teams', icon: Users },
  { value: 'jobtalk', label: 'JobTalk', icon: Building2 },
  { value: 'franchise', label: 'Franchise', icon: Building2 },
];

// Available tags for documents (can be expanded later)
const AVAILABLE_TAGS = [
  { value: 'jobtalk', label: 'JobTalk', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'franchise', label: 'Franchise', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'marketing', label: 'Marketing', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'sales', label: 'Sales', color: 'bg-orange-100 text-orange-700 border-orange-300' },
  { value: 'internal', label: 'Internal', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'external', label: 'External', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
];

const DOCUMENT_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'brochure', label: 'Brochure' },
  { value: 'flyer', label: 'Flyer' },
  { value: 'case_study', label: 'Case Study' },
  { value: 'pricing', label: 'Pricing Sheet' },
  { value: 'pitch_deck', label: 'Pitch Deck' },
  { value: 'one_pager', label: 'One-Pager' },
  { value: 'other', label: 'Other' },
];

const FILE_TYPES = [
  { value: 'all', label: 'All Formats' },
  { value: 'PDF', label: 'PDF' },
  { value: 'PNG', label: 'PNG' },
  { value: 'JPG', label: 'JPG/JPEG' },
  { value: 'PowerPoint', label: 'PowerPoint' },
  { value: 'Word', label: 'Word' },
];

const getFileIcon = (fileType) => {
  switch (fileType?.toUpperCase()) {
    case 'PDF':
      return <FileText className="h-8 w-8 text-red-500" />;
    case 'PNG':
    case 'JPG':
    case 'JPEG':
      return <Image className="h-8 w-8 text-blue-500" />;
    case 'POWERPOINT':
      return <FileText className="h-8 w-8 text-orange-500" />;
    case 'WORD':
      return <FileText className="h-8 w-8 text-blue-600" />;
    default:
      return <File className="h-8 w-8 text-slate-400" />;
  }
};

const getTeamTagBadge = (tags) => {
  if (!tags || tags.length === 0) return null;
  return tags.slice(0, 3).map(tag => {
    const tagConfig = AVAILABLE_TAGS.find(t => t.value === tag);
    if (!tagConfig) return null;
    return (
      <Badge 
        key={tag} 
        className={tagConfig.color + ' text-xs'}
      >
        {tagConfig.label}
      </Badge>
    );
  });
};

export default function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [formatFilter, setFormatFilter] = useState('all');
  
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    team_tags: [], // Array of selected tags
    document_type: 'other',
    version: '',
    description: ''
  });
  
  const fileInputRef = useRef(null);
  const replaceInputRef = useRef(null);

  useEffect(() => {
    loadDocuments();
  }, [teamFilter, typeFilter, formatFilter]);

  const loadDocuments = async (forceRefresh = false) => {
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (teamFilter !== 'all') params.append('team_tag', teamFilter);
      if (typeFilter !== 'all') params.append('document_type', typeFilter);
      if (formatFilter !== 'all') params.append('file_type', formatFilter);
      
      const res = await axios.get(`${API}/documents?${params.toString()}`);
      setDocuments(res.data.items || []);
      
      if (forceRefresh) toast.success('Documents refreshed');
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Document name is required');
      return;
    }
    if (formData.team_tags.length === 0) {
      toast.error('Please select at least one tag');
      return;
    }
    
    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', uploadFile);
      data.append('name', formData.name.trim());
      data.append('team_tags', formData.team_tags.join(','));
      data.append('document_type', formData.document_type);
      if (formData.version) data.append('version', formData.version);
      if (formData.description) data.append('description', formData.description);
      
      await axios.post(`${API}/documents`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded successfully');
      setUploadDialogOpen(false);
      resetForm();
      loadDocuments(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedDocument) return;
    
    try {
      await axios.put(`${API}/documents/${selectedDocument.id}`, {
        name: formData.name,
        team_tags: formData.team_tags,
        document_type: formData.document_type,
        version: formData.version || null,
        description: formData.description || null
      });
      
      toast.success('Document updated');
      setEditDialogOpen(false);
      loadDocuments(true);
    } catch (error) {
      toast.error('Failed to update document');
    }
  };

  const handleReplace = async () => {
    if (!selectedDocument || !replaceFile) return;
    
    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', replaceFile);
      
      await axios.put(`${API}/documents/${selectedDocument.id}/replace`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document file replaced');
      setReplaceDialogOpen(false);
      setReplaceFile(null);
      loadDocuments(true);
    } catch (error) {
      toast.error('Failed to replace file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocument) return;
    
    try {
      await axios.delete(`${API}/documents/${selectedDocument.id}`);
      toast.success('Document deleted');
      setDeleteDialogOpen(false);
      loadDocuments(true);
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handlePreview = (doc) => {
    setSelectedDocument(doc);
    setPreviewDialogOpen(true);
  };

  const openEditDialog = (doc) => {
    setSelectedDocument(doc);
    setFormData({
      name: doc.name,
      team_tags: doc.team_tags || [],
      document_type: doc.document_type,
      version: doc.version || '',
      description: doc.description || ''
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      team_tags: [],
      document_type: 'other',
      version: '',
      description: ''
    });
    setUploadFile(null);
    setSelectedDocument(null);
  };

  const toggleTag = (tagValue) => {
    setFormData(prev => ({
      ...prev,
      team_tags: prev.team_tags.includes(tagValue)
        ? prev.team_tags.filter(t => t !== tagValue)
        : [...prev.team_tags, tagValue]
    }));
  };

  const handleReset = () => {
    setSearchQuery('');
    setTeamFilter('all');
    setTypeFilter('all');
    setFormatFilter('all');
    toast.success('Filters cleared');
  };

  const filteredDocuments = useMemo(() => {
    if (!searchQuery) return documents;
    const q = searchQuery.toLowerCase();
    return documents.filter(doc => 
      doc.name.toLowerCase().includes(q) ||
      doc.file_name?.toLowerCase().includes(q)
    );
  }, [documents, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Document Library</h1>
          <p className="text-slate-500 mt-1">{documents.length} documents • Brochures, flyers, and marketing materials</p>
        </div>
        <Button onClick={() => { resetForm(); setUploadDialogOpen(true); }} data-testid="upload-document-btn">
          <Upload className="w-4 h-4 mr-2" /> Upload Document
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
            data-testid="search-documents"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>
        
        <Select value={teamFilter} onValueChange={setTeamFilter}>
          <SelectTrigger className="w-40">
            <Tag className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Team" />
          </SelectTrigger>
          <SelectContent>
            {TEAM_TAGS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            {DOCUMENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        
        <Select value={formatFilter} onValueChange={setFormatFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Format" />
          </SelectTrigger>
          <SelectContent>
            {FILE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        
        <Button variant="outline" size="icon" onClick={handleReset} title="Reset filters">
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" onClick={() => loadDocuments(true)} disabled={refreshing} title="Refresh">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">
              {searchQuery || teamFilter !== 'all' || typeFilter !== 'all' 
                ? 'No documents match your filters'
                : 'No documents yet. Upload your first document!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredDocuments.map(doc => (
            <Card key={doc.id} className="hover:shadow-md transition-shadow group">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getFileIcon(doc.file_type)}
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate" title={doc.name}>{doc.name}</p>
                      <p className="text-xs text-slate-500">{doc.file_size_formatted} • {doc.file_type}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handlePreview(doc)}>
                        <Eye className="h-4 w-4 mr-2" /> Preview
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDownload(doc)}>
                        <Download className="h-4 w-4 mr-2" /> Download
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditDialog(doc)}>
                        <Edit className="h-4 w-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setSelectedDocument(doc); setReplaceDialogOpen(true); }}>
                        <Replace className="h-4 w-4 mr-2" /> Replace File
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => { setSelectedDocument(doc); setDeleteDialogOpen(true); }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  {getTeamTagBadge(doc.team_tags)}
                  <Badge variant="outline" className="text-xs">{doc.document_type}</Badge>
                </div>
                
                {doc.version && (
                  <p className="text-xs text-slate-400 mb-2">Version: {doc.version}</p>
                )}
                
                <div className="flex items-center justify-between text-xs text-slate-400 pt-2 border-t">
                  <span>{format(parseISO(doc.created_at), 'MMM d, yyyy')}</span>
                  <span>{doc.use_count || 0} uses</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-indigo-600" />
              Upload Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* File Upload */}
            <div>
              <Label>File *</Label>
              <div 
                className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="h-6 w-6 text-indigo-600" />
                    <span className="font-medium">{uploadFile.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); setUploadFile(null); }}>
                      <X className="h-4 w-4 text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">Click to select file</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, PNG, JPG, PPTX, DOCX (max 100MB)</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.pptx,.docx,.xlsx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setUploadFile(file);
                    if (!formData.name) {
                      setFormData(f => ({ ...f, name: file.name.replace(/\.[^/.]+$/, '') }));
                    }
                  }
                }}
                className="hidden"
              />
            </div>
            
            <div>
              <Label>Document Name *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., JobTalk Brochure 2026"
              />
            </div>
            
            {/* Tags Selector */}
            <div>
              <Label>Tags *</Label>
              {/* Selected tags */}
              {formData.team_tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 mb-2">
                  {formData.team_tags.map(tagValue => {
                    const tagConfig = AVAILABLE_TAGS.find(t => t.value === tagValue);
                    if (!tagConfig) return null;
                    return (
                      <Badge 
                        key={tagValue} 
                        className={`${tagConfig.color} border cursor-pointer pr-1`}
                      >
                        {tagConfig.label}
                        <button 
                          type="button"
                          onClick={() => toggleTag(tagValue)}
                          className="ml-1 hover:bg-black/10 rounded p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
              {/* Available tags to add */}
              <div className="flex flex-wrap gap-2 mt-2">
                {AVAILABLE_TAGS.filter(t => !formData.team_tags.includes(t.value)).map(tag => (
                  <Badge 
                    key={tag.value}
                    variant="outline"
                    className="cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => toggleTag(tag.value)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {tag.label}
                  </Badge>
                ))}
              </div>
              {formData.team_tags.length === 0 && (
                <p className="text-xs text-slate-500 mt-1">Click tags above to add them</p>
              )}
            </div>

            <div>
              <Label>Document Type</Label>
              <Select value={formData.document_type} onValueChange={(val) => setFormData({ ...formData, document_type: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.filter(t => t.value !== 'all').map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Version (optional)</Label>
              <Input 
                value={formData.version} 
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                placeholder="e.g., v2.0"
              />
            </div>
            
            <div>
              <Label>Description (optional)</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this document..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-indigo-600" />
              Edit Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Document Name *</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            
            {/* Tags Selector */}
            <div>
              <Label>Tags *</Label>
              {/* Selected tags */}
              {formData.team_tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 mb-2">
                  {formData.team_tags.map(tagValue => {
                    const tagConfig = AVAILABLE_TAGS.find(t => t.value === tagValue);
                    if (!tagConfig) return null;
                    return (
                      <Badge 
                        key={tagValue} 
                        className={`${tagConfig.color} border cursor-pointer pr-1`}
                      >
                        {tagConfig.label}
                        <button 
                          type="button"
                          onClick={() => toggleTag(tagValue)}
                          className="ml-1 hover:bg-black/10 rounded p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
              {/* Available tags to add */}
              <div className="flex flex-wrap gap-2 mt-2">
                {AVAILABLE_TAGS.filter(t => !formData.team_tags.includes(t.value)).map(tag => (
                  <Badge 
                    key={tag.value}
                    variant="outline"
                    className="cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => toggleTag(tag.value)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {tag.label}
                  </Badge>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Document Type</Label>
              <Select value={formData.document_type} onValueChange={(val) => setFormData({ ...formData, document_type: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.filter(t => t.value !== 'all').map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Version</Label>
              <Input 
                value={formData.version} 
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdate}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace File Dialog */}
      <Dialog open={replaceDialogOpen} onOpenChange={setReplaceDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Replace className="h-5 w-5 text-indigo-600" />
              Replace File
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Replace the file for "<strong>{selectedDocument?.name}</strong>" while keeping the same document record.
            </p>
            <div 
              className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-indigo-400 cursor-pointer"
              onClick={() => replaceInputRef.current?.click()}
            >
              {replaceFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="h-6 w-6 text-indigo-600" />
                  <span className="font-medium">{replaceFile.name}</span>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Select new file</p>
                </>
              )}
            </div>
            <input
              ref={replaceInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.pptx,.docx,.xlsx"
              onChange={(e) => setReplaceFile(e.target.files[0])}
              className="hidden"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReplaceDialogOpen(false); setReplaceFile(null); }}>Cancel</Button>
            <Button onClick={handleReplace} disabled={!replaceFile || uploading}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Replace className="w-4 h-4 mr-2" />}
              Replace
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.name}</DialogTitle>
          </DialogHeader>
          <div className="h-[70vh] overflow-hidden">
            {selectedDocument && ['PDF', 'PNG', 'JPG', 'JPEG'].includes(selectedDocument.file_type?.toUpperCase()) ? (
              <iframe
                src={`${API}/documents/${selectedDocument.id}/preview`}
                className="w-full h-full border-0"
                title="Document Preview"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <File className="h-16 w-16 mb-4" />
                <p>Preview not available for this file type</p>
                <Button className="mt-4" onClick={() => handleDownload(selectedDocument)}>
                  <Download className="h-4 w-4 mr-2" /> Download to View
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "<strong>{selectedDocument?.name}</strong>"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
